import React from "react";
import { useTranslations } from "next-intl";
import {
  Stack,
  FormControl,
  Paper,
  Button,
  Grid,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { CustomTextfieldController } from "@cbex/form/textfield";
import { WcDateTimePickerController } from "@bpipartners/hookformcontroller";
import { UseFormReturn, useWatch } from "react-hook-form";
import { ExamAddressesAutoComplete } from "../composites/../composites/Refactor/ExamAddressesAutoComplete";
import ExamResourcesAutoComplete from "../composites/Refactor/ExamResourcesAutoComplete";
import { AppContext } from "@cbex/utils/context";
import {
  ResourcesByActivityTypeIdAndTypeIdRow,
  RelationAddress,
  GetActivityTypeDocument,
  PlanningActivityType,
  ResourceListByActivityTypeIdDocument,
} from "@cbex/data-access";
import { useQuery } from "@apollo/client";
import { useWindowDimensions } from "@cbex/utils/hooks";
import { CustomTextField } from "@cbex/ui/input";
import { CustomDialog } from "@cbex/ui/modal";
import DeleteIcon from "@mui/icons-material/Delete";
import { CustomTypographyComponent } from "@cbex/ui/text";
import { useTheme } from "@cbex/utils/theme";
import { ExamDetailsProp } from "./ExamDetailsForm";
import { CustomNumberController } from "@cbex/form/numberfield";
import { CustomCheckboxController } from "@cbex/form/checkbox";
import {
  getActivitiesImmutable,
  getActivitiesCreationNotAllowed,
} from "../utils/getExamEditState";
import { Wc } from "@mui/icons-material";

interface NewExamFormProps {
  form: UseFormReturn<ExamDetailsProp>;
  onActivityChange: (outGoingActivity: any) => void;
  newAddresCreated?: boolean;
  relationAddress?: RelationAddress;
}

const removeInArray = (
  a: PlanningActivityType[],
  b: PlanningActivityType[]
) => {
  const newArrr = a.filter(
    (ar) => !b.find((rm) => rm.activityTypeID === ar.activityTypeID)
  );
  return newArrr;
};

const intersection = (a: readonly any[], b: readonly any[]) =>
  a.filter((value: any) =>
    b.find(
      (bt) => bt.activityTypeID.toString() === value.activityTypeID.toString()
    )
  );

const not = (a: readonly any[], b: readonly any[]) =>
  a.filter((value: any) => b.indexOf(value) === -1);

const NewExamForm = ({
  form,
  onActivityChange,
  relationAddress,
  newAddresCreated,
}: NewExamFormProps) => {
  const windowSize = useWindowDimensions();
  const t = useTranslations();
  const { control, setValue, formState, trigger } = form;
  const { errors, isDirty, isSubmitted } = formState;
  const theme = useTheme();
  const [resources, setResources] = React.useState<
    ResourcesByActivityTypeIdAndTypeIdRow[]
  >([]);
  //@ts-ignore
  const { localAppState } = React.useContext(AppContext);
  const { enableOpenExamCreation } =
    localAppState?.userData?.organization?.properties;
  const [planningActivityTypes, setPlanningActivityTypes] = React.useState<
    PlanningActivityType[]
  >([]);
  const [activityTypeIDs, setActivityTypeIDs] = React.useState([]);
  const publicExamValue = useWatch({ control, name: "publicExam" });
  const activityTypeIDsValue = useWatch({ control, name: "activityTypes" });
  const startDateValue = useWatch({ control, name: "moment.startDate" });

  const localChanges = useWatch({
    control,
    name: ["activityTypes"],
  });

  React.useEffect(() => {
    if (startDateValue !== undefined) {
      // @ts-ignore
      trigger("activityTypeIDs");
    }
  }, [startDateValue, activityTypeIDsValue]);

  // left hand side  and also part of the search
  const [localPlanningActivityTypes, setLocalPlanningActivityTypes] =
    React.useState<PlanningActivityType[]>([]);
  // right hand side for dispalying
  const [selectedActivityTypes, setSelectedActivityTypes] = React.useState<
    PlanningActivityType[]
  >([]);
  const [searchText, setSearchText] = React.useState<string | null>(null);

  const [checked, setChecked] = React.useState<readonly any[]>([]);
  const [
    currentDeletePendingActivityType,
    setCurrentDeletePendingActivityType,
  ] = React.useState<PlanningActivityType[]>([]);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] =
    React.useState(false);

  const leftChecked =
    planningActivityTypes && planningActivityTypes.length > 0
      ? intersection(checked, planningActivityTypes)
      : [];

  const handleToggle = (value: any) => () => {
    if (checked.length > 0) {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];

      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
    } else {
      setChecked([value]);
    }
  };

  const handleCancel = () => {
    setOpenDeleteConfirmDialog(false);
    setCurrentDeletePendingActivityType(null);
  };

  const handleCheckedRight = () => {
    const newSelected = selectedActivityTypes.concat(...leftChecked);
    setSelectedActivityTypes(newSelected);
    setValue("activityTypes", newSelected);
    const newLocal = removeInArray(planningActivityTypes, newSelected);
    setLocalPlanningActivityTypes(newLocal);
    setChecked(not(checked, leftChecked));
    setSearchText(null);
  };

  const filter = (incomingVaL) => {
    setSearchText(incomingVaL);

    const temp = planningActivityTypes.filter((val: PlanningActivityType) =>
      val.name.toLowerCase().includes(incomingVaL)
    );
    const temp2 = removeInArray(temp, selectedActivityTypes);

    setLocalPlanningActivityTypes(temp2);
  };

  const isActivityImmutable = (activityTypeID: string): boolean =>
    getActivitiesImmutable(planningActivityTypes, startDateValue).some(
      (activity) => activity.activityTypeID === activityTypeID
    );

  const isActivityCreationNotAllowed = (activityTypeID: string): boolean =>
    getActivitiesCreationNotAllowed(
      planningActivityTypes,
      startDateValue,
      false
    ).some((activity) => activity.activityTypeID === activityTypeID);

  const handleDisabledFilter = (option, inputArray): boolean => {
    if (!startDateValue) {
      // We need to select a start date first before any activity can be selected
      return true;
    }

    if (
      isActivityImmutable(option.activityTypeID) ||
      isActivityCreationNotAllowed(option.activityTypeID)
    ) {
      return true;
    }

    if (inputArray.length <= 0) {
      return false;
    }

    if (option) {
      const findCometenceID = inputArray[0].competenceID;

      if (findCometenceID === option.competenceID) return false;
      else {
        return true;
      }
    }
  };

  const handleDelete = () => {
    const sortlocalPlanningActivityTypes = localPlanningActivityTypes.concat(
      currentDeletePendingActivityType
    );

    const newActivityArrayAfterDelete = removeInArray(
      selectedActivityTypes,
      currentDeletePendingActivityType
    );

    sortlocalPlanningActivityTypes.sort(function (a, b) {
      const textA = a.name.toUpperCase();
      const textB = b.name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
    setLocalPlanningActivityTypes(sortlocalPlanningActivityTypes);

    setSelectedActivityTypes(newActivityArrayAfterDelete);
    setValue("activityTypes", newActivityArrayAfterDelete);
    setChecked(not(checked, currentDeletePendingActivityType));

    if (newActivityArrayAfterDelete.length <= 0) {
      setValue("resources", []);
      setValue("locationID", null);
    }

    setCurrentDeletePendingActivityType([]);
    setOpenDeleteConfirmDialog(false);
  };

  const { data: activityTypeData, loading: activityTypeDataLoading } = useQuery(
    GetActivityTypeDocument,
    {
      variables: {},
      fetchPolicy: "network-only",
    }
  );

  const { data: resourcesData, loading: resourcesLoading } = useQuery(
    ResourceListByActivityTypeIdDocument,
    {
      variables: {
        activityTypeID:
          (localChanges &&
            localChanges[0] &&
            localChanges[0] &&
            localChanges[0].map((item) => item.activityTypeID)) ||
          [],
      },
      fetchPolicy: "network-only",
    }
  );

  React.useEffect(() => {
    if (
      !activityTypeDataLoading &&
      activityTypeData &&
      activityTypeData.ActivityTypes &&
      activityTypeData.ActivityTypes.length > 0
    ) {
      const newActivityType = activityTypeData.ActivityTypes.map(
        (item: PlanningActivityType) => ({
          activityTypeID: item.activityTypeID,
          name: item.name,
          competenceID: item.competenceID,
          settings: item.settings,
        })
      );

      const activityTypeIds = activityTypeData.ActivityTypes.map(
        (item) => item.activityTypeID
      );
      setPlanningActivityTypes([...newActivityType]);

      setActivityTypeIDs([...activityTypeIds]);
      setLocalPlanningActivityTypes(
        removeInArray([...newActivityType], selectedActivityTypes)
      );
    } else {
      setPlanningActivityTypes([]);
      setActivityTypeIDs([]);
      setLocalPlanningActivityTypes([]);
    }
  }, [activityTypeData, activityTypeDataLoading]);

  React.useEffect(() => {
    if (
      resourcesData &&
      resourcesData.ResourceListByActivityTypeID &&
      resourcesData.ResourceListByActivityTypeID.length > 0
    ) {
      const resourceList: ResourcesByActivityTypeIdAndTypeIdRow[] =
        resourcesData.ResourceListByActivityTypeID.map(
          (resourceInput: { name: string; resourceID: string }) => ({
            employeeID: null,
            name: resourceInput.name,
            resourceID: resourceInput.resourceID,
            rank: 0,
          })
        );

      setResources(resourceList);
    } else {
      setResources([]);
    }
  }, [resourcesData, resourcesLoading]);

  React.useEffect(() => {
    if (localChanges) {
      onActivityChange(localChanges[0]);
    }
  }, [localChanges, onActivityChange]);

  const customList = (
    title: React.ReactNode,
    items: readonly PlanningActivityType[],
    isSelectedActivitiesList: Boolean
  ) => (
    <Card sx={{ width: 500 }}>
      <CardHeader
        sx={{
          px: 2,
          py: 1,
        }}
        subheader={title}
        subheaderTypographyProps={{
          color:
            (isDirty || isSubmitted) && errors && errors["activityTypeIDs"]
              ? theme.appColors.error
              : null,
        }}
      />
      {!isSelectedActivitiesList && (
        <CustomTextField
          variant="filled"
          label={t("labels.searchActivityType")}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            filter(event.target.value.toLocaleLowerCase());
          }}
          value={searchText}
          type="search"
        />
      )}
      <Divider />
      {items && items.length > 0 && (
        <List
          sx={{
            width: 490,
            maxHeight: 190,
            bgcolor: "background.paper",
            overflow: "auto",
          }}
          dense
          component="div"
          role="list"
        >
          {items.map((value: any) => {
            const labelId = `transfer-list-all-item-${value.name}-label`;

            return (
              <ListItemButton
                disableRipple
                key={value.activityTypeID}
                disabled={
                  !isSelectedActivitiesList
                    ? handleDisabledFilter(value, selectedActivityTypes) ||
                      handleDisabledFilter(value, checked)
                    : false
                }
                onClick={
                  !isSelectedActivitiesList ? handleToggle(value) : () => {}
                }
              >
                <ListItemIcon>
                  {!isSelectedActivitiesList && (
                    <Checkbox
                      checked={checked.includes(value)}
                      disableRipple
                      size="small"
                      disabled={
                        handleDisabledFilter(value, selectedActivityTypes) ||
                        handleDisabledFilter(value, checked)
                      }
                    />
                  )}
                </ListItemIcon>
                <ListItemText key={labelId} primary={`${value.name}`} />
                {isSelectedActivitiesList && (
                  <ListItemSecondaryAction>
                    <Tooltip title={t("labels.delete")}>
                      {/* span added so as to activate the tooltip for disabled button on hover */}
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setCurrentDeletePendingActivityType([value]);
                            setOpenDeleteConfirmDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItemSecondaryAction>
                )}
              </ListItemButton>
            );
          })}
          <ListItem />
        </List>
      )}

      {openDeleteConfirmDialog && (
        <CustomDialog
          open={openDeleteConfirmDialog}
          leftButtonHeading={t("labels.cancel")}
          rightButtonHeading={t("labels.delete")}
          handleCancel={handleCancel}
          handleClosePressed={handleDelete}
          heading={t("common.deleteActivityConfirm")}
        />
      )}
    </Card>
  );

  return (
    <Paper>
      <Stack
        direction={"row"}
        padding={2}
        sx={{
          maxHeight: windowSize.height - 400,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Stack direction="column" spacing={2}>
          <FormControl sx={{ width: "500px" }}>
            <WcDateTimePickerController
              label={t("labels.startDate")}
              name="moment.startDate"
              control={control}
              required
              error={errors && errors["moment.startDate"]}
            />
          </FormControl>
          <FormControl variant="filled">
            <Grid>
              <Grid>{customList("", localPlanningActivityTypes, false)}</Grid>
            </Grid>
          </FormControl>
          {!newAddresCreated && (
            <FormControl variant="filled">
              <ExamAddressesAutoComplete
                name={"locationID"}
                control={control}
                required
                errors={errors && errors["locationID"]}
                inputSelectedActivityTypes={
                  (localChanges && localChanges[0]) || []
                }
                form={form}
              />
            </FormControl>
          )}

          {enableOpenExamCreation && (
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              spacing={2}
              padding={2}
              width={"100%"}
            >
              <CustomCheckboxController
                label={t("labels.publicExam")}
                control={control}
                name="publicExam"
              />
              <CustomNumberController
                control={control}
                name={"publicExamSeats"}
                label={t("labels.publicExamSeats")}
                disabled={!form.getValues("publicExam")}
                sx={{ width: "140px" }}
              />
            </Stack>
          )}
          {newAddresCreated && relationAddress && (
            <Stack width={"100%"}>
              {relationAddress.name && (
                <CustomTypographyComponent>
                  {relationAddress.name}
                  {", "}
                </CustomTypographyComponent>
              )}
              <CustomTypographyComponent>
                {relationAddress.street} {relationAddress.number}{" "}
                {relationAddress.postalcode} {relationAddress.city}
              </CustomTypographyComponent>
            </Stack>
          )}
          <FormControl variant="filled">
            <ExamResourcesAutoComplete
              name={"resources"}
              control={control}
              errors={errors["resources"]}
              resources={resources}
              required={resources && resources.length > 0}
              label="examiner"
              inputSelectedActivityTypes={
                (localChanges && localChanges[0] && localChanges[0]) || []
              }
            />
          </FormControl>
          <FormControl variant="outlined" sx={{ width: "500px" }}>
            <CustomTextfieldController
              control={control}
              name="notes"
              id="outlined-adornment-note"
              multiline
              label={t("labels.note")}
              rows={8}
              fullWidth
            />
          </FormControl>
        </Stack>
        {/* Activity transfer list button */}
        <Stack direction="column" marginTop={48} marginLeft={4}>
          <Tooltip
            title={
              leftChecked.length === 0
                ? t("labels.noActivitySelected")
                : t("common.addActivity")
            }
          >
            {/* span added so as to activate the tooltip for disabled button on hover */}
            <span>
              <Button
                sx={{ my: 0.5, height: 30 }}
                variant="contained"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
              >
                &gt;
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Stack direction={"column"} p={2}>
          <Grid>
            <Grid>
              {customList(
                t("common.mappedActivityList") + "*",
                selectedActivityTypes,
                true
              )}
            </Grid>
          </Grid>
          {(isDirty || isSubmitted) && errors && errors["activityTypeIDs"] && (
            <Stack>
              <CustomTypographyComponent color={theme.appColors.error}>
                {errors["activityTypeIDs"].message}
              </CustomTypographyComponent>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default NewExamForm;
